import { SendOutlined } from '@ant-design/icons/lib'
import {
  App,
  Button,
  Flex,
  Typography,
} from 'antd/es'
import React, {
  useCallback,
  useState,
} from 'react'

import { uploadImage } from '@/api/uploader'
import { Creator } from '@/model/creator'
import { MediaType } from '@/model/media'
import {
  defaultMessageDraftState,
  updateMessageDraft,
} from '@/redux/message-draft'
import { sendToPost } from '@/redux/send-post'
import {
  useAppDispatch,
  useAppSelector,
} from '@/redux/utils'
import base64ToFile from '@/utils/base64-to-file'
import loadImage from '@/utils/load-image'
import loadVideoCover from '@/utils/load-video-cover'

import { selectMessageDraft } from '../selector'

import PostMessageInput from './post-message-input'
import s from './send-post.module.css'


const { Title } = Typography

export interface SendPostProps {
  creator: Creator
}

export default function SendPost({ creator }: SendPostProps) {
  const { medias, text, price, sendMediaType, cover } = useAppSelector((state) =>
    selectMessageDraft(state, creator.id, creator.onlyFansUserId),
  )

  const dispatch = useAppDispatch()
  const { message } = App.useApp()
  const [sendingLoading, setSendingLoading] = useState(false)

  const post = useCallback(async () => {
    if (medias.length === 0) {
      void message.info('Media is empty')
      return
    }

    const mediaUrl = []
    const width = []
    const height = []

    setSendingLoading(true)

    try {
      let mediaStr, widthStr, heightStr, genCover
      mediaStr = widthStr = heightStr = genCover = ''
      if (sendMediaType === MediaType.Image) {
        for (const item of medias) {
          if (item.src) {
            const img = await loadImage(item.src)
            mediaUrl.push(item.src)
            width.push(img.width)
            height.push(img.height)
          }
        }
        mediaStr = mediaUrl.join(',')
        widthStr = width.join(':')
        heightStr = height.join(':')
      } else if (sendMediaType === MediaType.Video) {
        const src = medias[0].src || ''
        const splitSrc = src.split(',') || []
        mediaStr = splitSrc.length > 1 ? splitSrc[1] : src

        if (cover) {
          const img = await loadImage(cover)
          widthStr = String(img.width)
          heightStr = String(img.height)
        } else {
          const generated = (await loadVideoCover(mediaStr)) || []
          genCover = await uploadImage({
            creatorId: creator.id,
            file: base64ToFile(generated[0] || ''),
          })
          widthStr = String(generated[1] ?? '0')
          heightStr = String(generated[2] ?? '0')
        }
      }

      await dispatch(
        sendToPost({
          creatorId: creator.id,
          media: mediaStr,
          title: text,
          type: sendMediaType,
          currency: 0,
          price: Number(price) || 0,
          userid: creator.onlyFansUserId,
          width: widthStr,
          height: heightStr,
          cover: cover || genCover,
        }),
      ).unwrap()

      void message.success('Send successfully.')
      void dispatch(
        updateMessageDraft({
          creatorId: creator.id,
          fanUserId: creator.onlyFansUserId,
          changes: defaultMessageDraftState,
        }),
      )
    } finally {
      setSendingLoading(false)
    }
  }, [
    cover,
    creator.id,
    creator.onlyFansUserId,
    dispatch,
    medias,
    message,
    price,
    sendMediaType,
    text,
  ])

  return (
    <Flex className={s.SendPost}>
      <Flex justify="space-between" align="center">
        <Title level={3}>New Post</Title>
        <Button
          loading={sendingLoading}
          disabled={sendingLoading}
          shape="round"
          type="primary"
          icon={<SendOutlined />}
          onClick={post}
        >
          Post
        </Button>
      </Flex>
      <PostMessageInput
        creatorId={creator.id}
        creatorPlatformUserId={creator.onlyFansUserId}
        fanUserId={creator.onlyFansUserId}
      />
    </Flex>
  )
}
